/**
 * 表格文本可选 — 对抗 React Aria usePress 在 pointerdown 写入的 inline `user-select: none`。
 * Guard：`DataTableTextSelectionGuard`；行点选+拖选并存：`data-table-selection-text-harmony`。
 */

import type { PointerEvent as ReactPointerEvent } from "react";

/** 单元格 / 表头标签：可选取样式（勿用 !important；与 Guard 清 inline 锁配合）。 */
export const copyableTableCellClassName = "select-text cursor-text";

/**
 * 阻止行级 press（预览/操作菜单等）。
 * 正文单元格若需「行点选 + 拖选」并存，勿用本函数 — 改用
 * `useDataTableSelectionWithTextSelect`。
 */
export const preventTableRowSelectionOnPointerDown = (
  event: ReactPointerEvent<HTMLElement>,
) => {
  event.stopPropagation();
};

/** Table 根（清 press 锁）。 */
const TABLE_ROOT_SELECTOR = ':is([data-slot="table"], .table-root)';

const PRESS_LOCK_SELECTOR = [
  '[data-slot="table-column"]',
  ".table__column",
  '[data-slot="table-row"]',
  ".table__row",
  '[data-slot="table-cell"]',
  ".table__cell",
  "tr",
  "th",
  "td",
].join(", ");

const RESIZE_SELECTOR =
  '[data-slot="table-column-resizer"], .table__column-resizer, [role="separator"]';

const clearInlineUserSelect = (element: HTMLElement) => {
  const property = "userSelect" in element.style ? "userSelect" : "webkitUserSelect";
  if (element.style[property] !== "none") return;
  element.style[property] = "";
  if (element.getAttribute("style") === "") {
    element.removeAttribute("style");
  }
};

/**
 * 在 React Aria usePress 写入 inline `user-select: none` 之后同步清除（同一 pointerdown 派发内）。
 * 不使用 `!important`；列宽拖动手柄上保留 none。
 */
export const clearDataTablePressUserSelectLock = (
  eventTarget: EventTarget | null,
): void => {
  if (!(eventTarget instanceof Element)) return;
  if (eventTarget.closest(RESIZE_SELECTOR)) return;

  const tableRoot = eventTarget.closest(TABLE_ROOT_SELECTOR);
  if (!tableRoot) return;

  let node: Element | null = eventTarget;
  while (node && tableRoot.contains(node)) {
    if (node instanceof HTMLElement && node.matches(PRESS_LOCK_SELECTOR)) {
      clearInlineUserSelect(node);
    }
    if (node === tableRoot) break;
    node = node.parentElement;
  }
};
